import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Gauge, BrainCircuit, Zap, Flame, Heart, Brain, Sparkles, FlaskConical } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import taurineGraph from '@/assets/taurine-enzyme-activity.png';
import betaineGraph from '@/assets/betaine-endurance-study.png';
import tyrosineGraph from '@/assets/tyrosine-brain-transport.png';

const DetailedInfoMaxed = () => {
  return (
    <Card className="bg-card backdrop-blur-sm border border-border mb-6">
      <CardHeader className="pb-3 md:pb-4">
        <CardTitle className="text-xl md:text-2xl font-bold mb-1 text-primary leading-tight font-orbit">
          Peveo Maxed 700g: Ekstrem formular for maksimal intensitet
        </CardTitle>
      </CardHeader>
      <CardContent className="prose prose max-w-none pt-0 space-y-4 md:space-y-6">
        <p className="text-base md:text-lg font-medium text-foreground leading-snug">
          Peveo Maxed er designet for de som vil ha maksimal intensitet. Med 350mg koffein, 6.4g beta-alanine 
          og 15g L-Citrulline Malate er dette Norges høyest doserte pre-workout. Alle ingredienser er 
          vitenskapelig dokumentert og dosert etter kliniske studier.
        </p>
        
        <div className="bg-muted p-4 md:p-6 rounded-lg border border-border">
          <h3 className="text-lg md:text-xl font-bold text-primary mb-2 md:mb-3 flex items-center gap-2">
            <Gauge className="text-primary" size={18} />
            Peveo Maxed vs. andre produkter
          </h3>
          <p className="mb-2 md:mb-3 text-foreground text-sm md:text-base leading-snug">
            Dette er den mest intense formularen på markedet. Med 350mg koffein per full dose 
            og ekstreme doser av alle aktive ingredienser, er dette produktet for erfarne brukere.
          </p>
          <p className="text-foreground text-sm md:text-base leading-snug">
            Denne ble starten på vår suksess i 2023 og er laget for de som vil ha <span className="text-primary font-semibold">maksimal effekt</span>.
          </p>
        </div>
        
        <h2 className="text-xl md:text-2xl font-bold text-primary flex items-center gap-2 mt-4 md:mt-6">
          <BrainCircuit className="text-primary" size={22} />
          Ingredienser breakdown
        </h2>

        <div className="space-y-4 md:space-y-6">
          {/* L-Citrulline Malate Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">L-Citrulline-DL-Malate 2:1 – 15,000 mg</h3>
            <p className="mb-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              L-Citrulline Malate er det mest effektive tilskuddet for å øke nitrogenoksid (NO) i kroppen. 
              Med 15g Citrulline Malate (10g ren L-Citrulline + 5g Malic Acid) får du den optimale doseringen for maksimal pump.
            </p>
            
            <div className="grid md:grid-cols-2 gap-3 md:gap-4 mb-3">
              <div>
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Hvorfor nitrogenoksid er viktig:</h4>
                <ul className="list-disc list-inside space-y-1 md:space-y-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                  <li>Utvider blodkar (vasodilatasjon)</li>
                  <li>Senker blodtrykket</li>
                  <li>Øker oksygennivået i blodet</li>
                  <li>Sikrer effektiv blodstrøm til muskler</li>
                  <li>Maksimal muskelpump under trening</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Dokumenterte effekter:</h4>
                <ul className="list-disc list-inside space-y-1 md:space-y-2 text-xs md:text-base text-muted-foreground leading-relaxed">
                  <li>9% økt prestasjon i underkroppen <a href="https://pubmed.ncbi.nlm.nih.gov/25226311/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
                  <li>19% økt prestasjon i overkroppen <a href="https://pubmed.ncbi.nlm.nih.gov/25674699/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
                  <li>Redusert muskeltretthet</li>
                  <li>40% reduksjon i muskelsmerter etter trening <a href="https://pubmed.ncbi.nlm.nih.gov/20386132/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start mt-4">
              <div className="flex-shrink-0 w-full md:w-1/2">
                <img 
                  src="/lovable-uploads/e01b43fb-67c8-4135-bfb7-75c53a26efd0.png" 
                  alt="Citrulline's systemiske effekter diagram" 
                  className="w-full rounded-lg border border-border"
                />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Citrulline's systemiske effekter:</h4>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Diagram som viser hvordan L-citrulline metaboliseres i kroppen for å øke nitrogenoksid produksjonen. 
                  5kg vannmelon inneholder kun rundt 3g L-citrulline <a href="https://pubmed.ncbi.nlm.nih.gov/30029482/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">[R]</a>.
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* Beta-Alanine Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Zap className="text-primary" size={20} />
              Beta-Alanine – 6,400 mg
            </h3>
            <p className="mb-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              Beta-Alanine øker karnosinlagre i musklene, som fungerer som en buffer mot melkesyre. 
              Dette utsetter utmattelse og forbedrer utholdenhet under høyintensiv trening.
            </p>
            
            <div className="bg-muted p-3 md:p-4 rounded-lg">
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>2.85% forbedring i treningskapasitet ved aktiviteter 1-4 minutter</li>
                <li>Signifikant økt utholdenhet under høyintensiv trening</li>
                <li>Reduserer oppbygging av melkesyre i muskler</li>
                <li>Optimal dose: 3.2-6.4g daglig for maksimal effekt <a href="https://pubmed.ncbi.nlm.nih.gov/20091069/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
              </ul>
            </div>

            <div className="bg-primary/10 p-3 md:p-4 rounded-lg mt-3 border border-border">
              <h4 className="font-bold text-primary mb-2 text-sm md:text-base">Om "tingling" (parestesi):</h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Den karakteristiske kløen/priklingen fra beta-alanine er ufarlig og skyldes aktivering av sensoriske nevroner. 
                Dette er et tegn på at produktet virker og avtar med regelmessig bruk.
              </p>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* Creatine Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Flame className="text-primary" size={20} />
              Creatine Monohydrate – 5 000 mg
            </h3>
            <p className="mb-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              Daglig vedlikeholdsdose for å opprettholde mettede kreatinlagre i muskulaturen.
            </p>
            
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-3">
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Effekt dokumentert i meta-analyser og posisjonserklæringer:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li><span className="text-foreground font-semibold">Økt muskelstyrke og kraft:</span> En meta-analyse fant at kreatin forbedrer både maksimal styrke og muskelkraft (<a href="https://pubmed.ncbi.nlm.nih.gov/28615996/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Kreider et al., 2017</a>). ISSN konkluderer med at det er det mest effektive kosttilskuddet tilgjengelig for å øke kapasitet ved høyintensiv trening og mager muskelmasse.</li>
                <li><span className="text-foreground font-semibold">Kognitiv funksjon:</span> Forskning viser at kreatin kan forbedre kognitiv funksjon, spesielt under forhold med søvnmangel eller mentalt stress, samt hos eldre (<a href="https://pubmed.ncbi.nlm.nih.gov/30068344/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Avgerinos et al., 2018</a>).</li>
              </ul>
            </div>

            <div className="bg-muted p-3 md:p-4 rounded-lg mb-3 border border-border">
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">I en Pre-Workout:</h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Effekten av kreatin er primært kronisk gjennom metning av lagrene, ikke akutt. Inntak i en PWO er en trygg og praktisk måte å sikre den daglige vedlikeholdsdosen på, selv om timingen ikke er kritisk for den umiddelbare økten.
              </p>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/28615996/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Kreider et al. (2017)</a> – ISSN position stand: safety and efficacy of creatine supplementation</p>
              <p>Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/30068344/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Avgerinos et al. (2018)</a> – Effects of creatine supplementation on cognitive function</p>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* Betaine Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Heart className="text-primary" size={20} />
              Betaine (Trimethylglycine) – 2,500 mg
            </h3>
            <p className="mb-3 text-sm md:text-base text-muted-foreground">
              Betaine fungerer som en osmolytt i kroppen og bidrar til intracellulær hyperhydrering. 
              Ved å forbedre cellehydrering øker Betaine pumpen du oppnår under trening.
            </p>
            <div className="bg-muted p-3 md:p-4 rounded-lg">
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>Signifikant økning av styrke og kraftproduksjon</li>
                <li>Økning i repetisjoner på leg press og benkpress</li>
                <li>Potensial for økt muskelvekst og fettforbrenning</li>
                <li>Kan redusere risiko for hjerte-karsykdommer</li>
              </ul>
              <p className="mt-2 text-sm text-primary">
                Kilder: 
                <a href="https://pubmed.ncbi.nlm.nih.gov/20642826/" className="underline hover:text-primary/80 ml-1" target="_blank" rel="noopener noreferrer">Lee et al. (2010)</a>,
                <a href="https://pubmed.ncbi.nlm.nih.gov/19250531/" className="underline hover:text-primary/80 ml-1" target="_blank" rel="noopener noreferrer">Hoffman et al. (2009)</a>
              </p>
            </div>

            <div className="bg-muted p-3 md:p-4 rounded-lg mt-3 md:mt-4 border border-border">
              <h4 className="font-bold text-foreground mb-3 text-sm md:text-base">Visualisering av betainets effekt på muskelutholdenhet</h4>
              <img 
                src={betaineGraph} 
                alt="Graf som viser betainets effekt på antall repetisjoner i benpress og benkpress" 
                className="max-w-md mx-auto rounded-lg mb-3"
              />
              <p className="text-xs md:text-sm text-muted-foreground">
                <span className="font-semibold text-primary">Figur fra Arazi et al. (2022):</span> Grafen demonstrerer at deltakere som tok betaine presterte flere repetisjoner på både benpress og benkpress sammenlignet med placebo.
              </p>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* Taurine Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">Taurine – 2,000 mg</h3>
            <p className="mb-3 text-sm md:text-base text-muted-foreground">
              Taurin stabiliserer mitokondrielle funksjoner og fungerer som buffer i muskelceller. 
              Dette optimaliserer fettsyreforbrenning og utsetter tid til utmattelse under trening.
            </p>
            <div className="bg-muted p-3 md:p-4 rounded-lg">
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>Utsetter tid til utmattelse hos både trente og utrente</li>
                <li>Optimaliserer fettsyreforbrenning i mitokondrier</li>
                <li>Forbedrer cellehydrering og muskelfunksjon</li>
                <li>Akutt virkning - effekt allerede første dose</li>
              </ul>
              <p className="mt-2 text-sm text-primary">
                Kilde: 
                <a href="https://pubmed.ncbi.nlm.nih.gov/20804598/" className="text-primary underline hover:text-primary/80 ml-1" target="_blank" rel="noopener noreferrer">Hansen et al. (2010)</a>
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start mt-3 md:mt-4">
              <div className="flex-shrink-0 w-full md:w-1/3">
                <img 
                  src={taurineGraph} 
                  alt="Graf som viser taurinets effekt på enzymaktivitet" 
                  className="w-full rounded-lg border border-border"
                />
              </div>
              <div className="flex-1 bg-muted p-3 md:p-4 rounded-lg border border-border">
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Visualisering av taurinets effekt</h4>
                <p className="text-xs md:text-sm text-muted-foreground">
                  <span className="font-semibold text-primary">Figur fra Hansen et al. (2010):</span> Grafen viser hvordan enzymene som bryter ned fettsyrer (MCAD, LCAD, SCAD) blir betydelig mer aktive når taurin er tilstede, noe som øker musklenes evne til å brenne fett som energikilde.
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* L-Tyrosine Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Brain className="text-primary" size={20} />
              L-Tyrosine – 1,500 mg
            </h3>
            <p className="mb-3 text-sm md:text-base text-muted-foreground">
              L-Tyrosine er en aminosyre som fungerer som forløper for nevrotransmitterne dopamin, adrenalin og noradrenalin. 
              Virker spesielt godt ved akutt stress og kognitiv belastning.
            </p>
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-3">
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Forskningsresultater:</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>Forbedret mental skarphet og fokus under stress</li>
                <li>Økt kognitiv fleksibilitet og arbeidsminne</li>
                <li>Bedre humør og motivasjon ved akutt belastning</li>
                <li>Støtter dopamin- og noradrenalinsyntese</li>
              </ul>
              <p className="mt-2 text-sm text-primary">
                Kilder: 
                <a href="https://pubmed.ncbi.nlm.nih.gov/25598314/" className="underline hover:text-primary/80 ml-1" target="_blank" rel="noopener noreferrer">Steenbergen et al. (2015)</a>
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start mt-3 md:mt-4">
              <div className="flex-shrink-0 w-full md:w-1/3">
                <img 
                  src={tyrosineGraph} 
                  alt="Diagram som viser tyrosins transport over blod-hjerne-barrieren" 
                  className="w-full rounded-lg bg-white p-2 border border-border"
                />
              </div>
              <div className="flex-1 bg-muted p-3 md:p-4 rounded-lg border border-border">
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Hvordan tyrosin påvirker hjernen</h4>
                <p className="text-xs md:text-sm text-muted-foreground">
                  <span className="font-semibold text-primary">Figur fra Fernstrom & Fernstrom (2007):</span> Diagrammet viser at tyrosin-tilgjengelighet i hjernen (via transport over blod-hjerne-barrieren) er avgjørende for produksjon av dopamin og noradrenalin i aktive nevroner.
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* Caffeine Section */}
          <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
            <h3 className="text-lg md:text-xl font-semibold text-primary mb-3 flex items-center gap-2">
              <Zap className="text-primary" size={20} />
              Caffeine Anhydrous – 350 mg
            </h3>
            <p className="mb-4 text-sm md:text-base text-muted-foreground leading-relaxed">
              Verdens mest brukte og forskede prestasjonsfremmende stoff. Koffein blokkerer trøtthetssignaler, 
              forbedrer akutt muskelstyrke, og reduserer opplevd anstrengelse – men gir ikke ny energi.
            </p>

            {/* Kjernepoeng */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4 border border-border">
              <h4 className="font-bold text-primary mb-3 text-sm md:text-base">📌 Kjernepoeng fra forskningen</h4>
              <div className="grid md:grid-cols-2 gap-2">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Virker ved å <span className="font-semibold text-foreground">blokkere adenosin</span> (trøtthetssignalet) i hjernen – reduserer opplevd anstrengelse, men gir ikke ny energi.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Gir en <span className="font-semibold text-foreground">liten, men merkbar</span> akutt økning i maksimal styrke, spesielt for overkroppen.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Mer er ikke nødvendigvis bedre.</span> Effekt ses ofte ved lave doser (1,5–3 mg/kg), og økning til 6 mg/kg gir marginalt mer.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-amber-400">Timing er alt.</span> For å unngå å sabotere søvn og langtidsresultater, må inntaket skje mange timer før leggetid.
                  </p>
                </div>
              </div>
            </div>

            {/* 1. Hvordan virker det */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4">
              <h4 className="font-bold text-foreground mb-3 text-sm md:text-base flex items-center gap-2">
                <Brain className="text-primary" size={16} />
                1. Hvordan virker det?
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                Koffein har en nesten identisk molekylær struktur som <span className="font-semibold text-foreground">adenosin</span>, 
                det naturlige trøtthetssignalet. Ved å binde seg til adenosin-reseptorene i stedet, blokkerer koffein følelsen av 
                tretthet og øker mental skarphet. Den gir ikke ekstra energi, men hindrer din oppfatning av at du er sliten.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/26778018/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">McLellan et al. (2016)</a> – Caffeine's effects on cognitive, physical and occupational performance.
              </p>
            </div>

            {/* 2. Akutt effekt på styrke */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4">
              <h4 className="font-bold text-foreground mb-3 text-sm md:text-base flex items-center gap-2">
                <FlaskConical className="text-primary" size={16} />
                2. Akutt effekt på styrke (studiedata)
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground mb-3">
                En gjennomgang av studier viser en konsistent, men beskjeden, akutt effekt på styrke:
              </p>
              <div className="space-y-2">
                <div className="bg-muted p-3 rounded-lg border-l-2 border-cyan-500">
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-primary">Metaanalyse:</span> Koffein forbedret maksimal styrke med en effektstørrelse (SMD) på <span className="font-bold text-primary">0,20</span> og eksplosiv kraft med <span className="font-bold text-primary">0,17</span>.
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg border-l-2 border-cyan-500">
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-primary">Konkrete tall:</span> Enkeltstudier og tidligere analyser referert i gjennomgangen har vist <a href="https://pubmed.ncbi.nlm.nih.gov/28032417/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">~3% økning i 1RM knebøy</a> og rundt <a href="https://pubmed.ncbi.nlm.nih.gov/20019636/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">7% i isometrisk styrke</a>, selv om den samlede effekten varierer.
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg border-l-2 border-cyan-500">
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-primary">Overkropp vs. underkropp:</span> Effekten var signifikant for overkroppen (SMD=0,21), mens den samlede effekten for underkroppen (SMD=0,15) ikke nådde statistisk signifikans i denne analysen.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/30153266/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Grgic et al. (2018)</a> – Umbrella review of 21 meta-analyses.
              </p>
            </div>

            {/* 3. Optimal dosering */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4">
              <h4 className="font-bold text-foreground mb-3 text-sm md:text-base flex items-center gap-2">
                💊 3. Hvor mye trenger du? Optimal dosering
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Anbefalt område:</span> 3–6 mg per kg kroppsvekt er det vanlige anbefalte området.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Mindre kan være nok:</span> Effekt sees ofte allerede ved 1,5–3 mg/kg. For en 90 kg person tilsvarer dette 135–270 mg.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Mer gir marginal gevinst:</span> Studier som sammenligner 3 mg/kg og 6 mg/kg viser <a href="https://pubmed.ncbi.nlm.nih.gov/30900431/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">liten ytterligere forskjell</a>. Meget høye doser (9-11 mg/kg) gir ingen ekstra ytelsesfordel, men øker bivirkningsrisiko.
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg mt-2 border border-border">
                  <p className="text-xs md:text-sm text-muted-foreground">
                    💡 <span className="font-semibold">Vår anbefaling:</span> Start lavt (f.eks. 1–2 skjeer = 70-140 mg) for å teste toleranse. Du trenger sannsynligvis mindre enn du tror.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/31628266/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Grgic et al. (2020)</a> – Dose-response study; <a href="https://pubmed.ncbi.nlm.nih.gov/30900431/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Polito et al. (2019)</a> – 3 vs 6 mg/kg comparison.
              </p>
            </div>

            {/* 4. Langsiktig effekt */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4">
              <h4 className="font-bold text-foreground mb-3 text-sm md:text-base flex items-center gap-2">
                📈 4. Langsiktig effekt og treningstilpasning
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                Det er teorier om at koffein kan hjelpe langsiktig muskelvekst ved å muliggjøre høyere treningsvolum, 
                men forskningen er foreløpig begrenset. Det avgjørende er å sikre at koffeinbruken ikke forstyrrer søvn, som er avgjørende for restitusjon og vekst.
              </p>
            </div>

            {/* 5. Søvn og timing */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4 border border-amber-500/30">
              <h4 className="font-bold text-amber-400 mb-3 text-sm md:text-base flex items-center gap-2">
                ⚠️ 5. Timing for å unngå søvnproblemer
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground mb-3">
                For å unngå at den akutte fordelen ødelegger for søvnkvaliteten:
              </p>
              <div className="space-y-2 mb-3">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Redusert søvnkvalitet:</span> <a href="https://pubmed.ncbi.nlm.nih.gov/37948102/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Gardiner et al. (2023)</a> fant at koffein reduserer total søvntid med gjennomsnittlig 45 minutter og søvneffektiviteten med 7%.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Mindre dyp søvn:</span> Koffein reduserer spesifikt mengden av dyp søvn (N3), som er kritisk for fysisk restitusjon og muskeloppbygging.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Konsekvenser over tid:</span> <a href="https://pubmed.ncbi.nlm.nih.gov/38645678/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Bodur et al. (2025)</a> understreker at kronisk søvnforstyrrelse vil slå langt hardere negativt ut på prestasjon og helse enn den lille akutte styrkegevinsten.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Økt bivirkningsrisiko:</span> Bivirkninger som angst øker ved høyere doser. 350 mg tilsvarer ca. 5 mg/kg for en 70 kg person.
                  </p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-3 mb-3">
                <div className="bg-muted p-3 rounded-lg">
                  <h5 className="font-semibold text-foreground text-xs md:text-sm mb-2">🕐 "Cut-off"-tid</h5>
                  <p className="text-xs text-muted-foreground">
                    <a href="https://pubmed.ncbi.nlm.nih.gov/37948102/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Gardiner et al. (2023)</a> fant at en dose på 217,5 mg bør inntas minst 13,2 timer før leggetid. En dose på 350 mg bør inntas enda tidligere.
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg">
                  <h5 className="font-semibold text-foreground text-xs md:text-sm mb-2">⏰ Praktisk råd</h5>
                  <p className="text-xs text-muted-foreground">
                    Bruk koffein tidlig. Ta alltid dette produktet ved morgen- eller formiddagstrening. Unngå ettermiddags- eller kveldsøkter.
                  </p>
                </div>
              </div>
            </div>

            {/* Slik bruker du koffein riktig */}
            <div className="bg-muted p-3 md:p-4 rounded-lg mb-4 border border-border">
              <h4 className="font-bold text-primary mb-3 text-sm md:text-base flex items-center gap-2">
                ✅ Praktiske anbefalinger
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold text-sm">1.</span>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Start med lav dose.</span> Begynn med 1-2 norske skjeer (70-140 mg koffein) for å vurdere din toleranse. Du trenger sannsynligvis mindre enn du tror.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold text-sm">2.</span>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Bruk tidlig på dagen.</span> Ta alltid dette produktet ved morgen- eller formiddagstrening for å beskytte søvnkvaliteten.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold text-sm">3.</span>
                  <div className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Bland med PWO-Stim-added (koffeinfri).</span> Vi tilbyr også Peveo Competition PWO-Stim-added som er nesten identisk med dette produktet, men uten koffein. 
                    <span className="block mt-1">Bland halv dose av hver for en moderat koffeindose (175 mg) med full effekt av alle andre ingredienser.</span>
                    <span className="block mt-1 text-muted-foreground">🌙 Dette er den ideelle løsningen for trening senere på dagen!</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold text-sm">4.</span>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Respekter advarslene.</span> Ikke overskrid anbefalt dose. Ikke egnet for personer under 18 år, gravide eller ammende.
                  </p>
                </div>
              </div>
            </div>

            {/* Oppsummering */}
            <div className="bg-muted p-4 md:p-5 rounded-lg border border-border">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <h4 className="font-bold text-primary mb-2 text-sm md:text-base">Oppsummert</h4>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    <span className="font-bold text-foreground">350 mg koffein</span> er en høy dose som kan være effektiv for akutt prestasjon, 
                    men krever respekt og strategisk timing. Mindre kan være nok – effekt ses ofte ved lavere doser. For langsiktig suksess bør du alltid 
                    prioritere søvnkvalitet og derfor planlegge inntaket langt før leggetid, eller bruke vår koffeinfrie variant for senere treningsøkter.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-primary/10"></div>

          {/* Lion's Mane Section */}
          <div className="grid md:grid-cols-2 gap-3 md:gap-6">
            <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
              <h3 className="text-lg md:text-xl font-semibold text-primary mb-2 flex items-center gap-2">
                <Sparkles className="text-primary" size={20} />
                Lion's Mane Ekstrakt – 200 mg
              </h3>
              <p className="mb-3 text-sm md:text-base text-muted-foreground">
                En trygg, ikke-prestasjonsfremmende tilsetning. Forskningen viser ingen dokumentert akutt effekt på styrke eller utholdenhet. Primære studier fokuserer på dens langsiktige, potensielle rolle for kognitiv støtte og nervehelse, samt dens antioksidative egenskaper.
              </p>
              <div className="bg-muted p-3 md:p-4 rounded-lg mb-3">
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Kjernepoeng:</h4>
                <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                  <li>Ingen akutt treningsboost</li>
                  <li>Potensiell kognitiv støtte</li>
                  <li>Kilde til antioksidanter</li>
                  <li>God sikkerhetsprofil</li>
                </ul>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/30699216/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Vigna et al. (2019)</a> – Kognitiv støtte og humør</p>
                <p>Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/34840594/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Abdelshafy et al. (2021)</a> – Antioksidative egenskaper</p>
                <p>Ref: <a href="https://pubmed.ncbi.nlm.nih.gov/26244378/" className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer">Friedman (2015)</a> – Sikkerhet og næringsprofil</p>
              </div>
            </div>

            <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
              <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">Black Pepper Extract 95% Piperine – 10 mg</h3>
              <p className="mb-3 text-sm md:text-base text-muted-foreground">
                Piperine har sterk dokumentasjon for én ting: økt biotilgjengelighet av curcumin (gurkemeie). 
                For standard PWO-ingredienser som koffein, citrullin, beta-alanin og kreatin finnes det svært lite solid forskning som viser signifikant forbedret opptak i mennesker.
              </p>
              <div className="bg-muted p-3 md:p-4 rounded-lg">
                <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Ærlig vurdering:</h4>
                <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                  <li>Sterkt dokumentert for curcumin-absorpsjon (<a href="https://pubmed.ncbi.nlm.nih.gov/9619120/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Shoba et al., 1998</a>)</li>
                  <li>Mekanismen (enzymhemming) er reell, men utfallet for PWO-ingredienser er usikkert</li>
                  <li>Trygg ingrediens med marginal, teoretisk fordel</li>
                  <li>Forvent ikke merkbar forskjell i treningseffekt fra disse 10 mg</li>
                </ul>
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">
                Inkludert for potensielle synergier, men effekten i pre-workout kontekst er ofte overvurdert i markedsføring.
              </p>
            </div>
          </div>
        </div>
        
        <div className="h-px bg-primary/10 my-4 md:my-6"></div>
        
        {/* Usage Instructions */}
        <div className="bg-muted p-4 md:p-6 rounded-lg border border-border">
          <h3 className="text-lg md:text-xl font-semibold text-primary mb-3 flex items-center gap-2">
            <Zap className="text-primary" size={20} />
            Bruksanvisning
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Norsk servering (1-2 skjeer):</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>Bland 1-2 skjeer med 200-300ml kaldt vann</li>
                <li>Ta 20-30 minutter før trening</li>
                <li>Start med 1 skje for å vurdere toleranse</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Amerikansk servering (5 skjeer):</h4>
              <ul className="list-disc list-inside space-y-1 text-xs md:text-sm text-muted-foreground">
                <li>Bland 5 skjeer (35g) med 400-500ml kaldt vann</li>
                <li>Kun for erfarne brukere med høy koffeintoleranse</li>
                <li>Ikke overskrid anbefalt daglig dose</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-primary/10 p-3 rounded-lg mt-4 border border-border">
            <p className="text-xs md:text-sm text-primary font-medium">
              ⚠️ Ikke anbefalt for personer under 18 år, gravide eller ammende. 
              Ikke overskrid anbefalt daglig dose. Oppbevar utilgjengelig for barn.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DetailedInfoMaxed;
